import instance from "api/cmsapi";

const BATCH_SIZE = 8;

const TEST_DISTRICT_NAMES = new Set(["ao district 1", "ao district 2"]);

function isTestAprRegion(region) {
  const name = String(region?.name || "").trim().toLowerCase();
  const slug = String(region?.slug || "").trim().toLowerCase();
  return name === "ao" || name === "ao region" || slug === "ao" || slug === "ao-region";
}

function isTestAprDistrict(district, region) {
  if (isTestAprRegion(region)) return true;
  const name = String(district?.name || "").trim().toLowerCase();
  return TEST_DISTRICT_NAMES.has(name);
}

/** District rows under national org tree with parent region metadata. */
export function flattenDistrictsWithRegions(orgTree) {
  const out = [];
  for (const region of orgTree?.children || []) {
    if (isTestAprRegion(region)) continue;
    for (const d of region.children || []) {
      if (d?.level !== "district") continue;
      if (isTestAprDistrict(d, region)) continue;
      if (d.hasAccess === false) continue;
      const districtId = String(d.orgUnitId ?? d.id ?? "").trim();
      if (!districtId) continue;
      const regionKey = String(region.slug || region.orgUnitId || region.id || region.name || "");
      out.push({
        districtId,
        districtName: d.name || districtId,
        regionKey,
        regionName: region.name || regionKey,
      });
    }
  }
  return out;
}

/**
 * Same endpoint as APR_Report.jsx: GET assessments/dpat/{districtId}/{year}/APR
 */
export async function fetchDistrictAprStatuses(districtRows, year, signal, onProgress) {
  const y = String(year);
  const total = districtRows.length;
  const results = [];

  const reportProgress = (loaded) => {
    if (!onProgress || signal?.aborted) return;
    const percent = total === 0 ? 100 : Math.min(100, Math.round((loaded / total) * 100));
    onProgress({ loaded, total, percent });
  };

  reportProgress(0);

  if (total === 0) return results;

  for (let i = 0; i < districtRows.length; i += BATCH_SIZE) {
    if (signal?.aborted) break;
    const slice = districtRows.slice(i, i + BATCH_SIZE);
    const chunk = await Promise.all(
      slice.map(async (row) => {
        try {
          const res = await instance.get(`assessments/dpat/${row.districtId}/${y}/APR`, { signal });
          const st = res?.data?.status != null ? String(res.data.status).trim() : null;
          return { ...row, status: st };
        } catch {
          return { ...row, status: null };
        }
      }),
    );
    results.push(...chunk);
    reportProgress(results.length);
  }

  return results;
}

function pct(num, den) {
  if (!den || den <= 0) return "0.0";
  return ((Number(num) / den) * 100).toFixed(1);
}

function derivedStatus(rateStr) {
  const n = parseFloat(rateStr);
  if (n >= 70) return "excellent";
  if (n >= 40) return "good";
  if (n >= 15) return "warning";
  return "critical";
}

/** Labels aligned with APR_Report.jsx workflow (Start → Pending → Completed → Closed). */
export function getAprStatusMeta(status) {
  const st = status != null && String(status).trim() !== "" ? String(status).trim() : null;
  switch (st) {
    case "Start":
      return {
        key: "Start",
        label: "In progress (district)",
        shortLabel: "Start",
        submittedToRcc: false,
        badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
      };
    case "Pending":
      return {
        key: "Pending",
        label: "Submitted to RCC",
        shortLabel: "Pending",
        submittedToRcc: true,
        badgeClass: "bg-amber-50 text-amber-800 border-amber-200",
      };
    case "Completed":
      return {
        key: "Completed",
        label: "With NDPC (completed)",
        shortLabel: "Completed",
        submittedToRcc: true,
        badgeClass: "bg-sky-50 text-sky-800 border-sky-200",
      };
    case "Closed":
      return {
        key: "Closed",
        label: "Closed",
        shortLabel: "Closed",
        submittedToRcc: true,
        badgeClass: "bg-emerald-50 text-emerald-800 border-emerald-200",
      };
    default:
      return {
        key: "none",
        label: "Not started",
        shortLabel: "—",
        submittedToRcc: false,
        badgeClass: "bg-muted text-muted-foreground border-border",
      };
  }
}

export const APR_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "none", label: "Not started" },
  { value: "Start", label: "In progress (district)" },
  { value: "Pending", label: "Submitted to RCC" },
  { value: "Completed", label: "With NDPC" },
  { value: "Closed", label: "Closed" },
  { value: "submitted", label: "Any submitted (RCC+)" },
];

export function filterDistrictStatusRows(rows, { regionKey = "all", statusFilter = "all", search = "" }) {
  const q = search.trim().toLowerCase();
  return rows.filter((row) => {
    const meta = getAprStatusMeta(row.status);
    if (regionKey !== "all" && row.regionKey !== regionKey) return false;
    if (statusFilter === "submitted" && !meta.submittedToRcc) return false;
    if (statusFilter !== "all" && statusFilter !== "submitted" && meta.key !== statusFilter) return false;
    if (q) {
      const hay = `${row.districtName} ${row.regionName} ${meta.label}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function buildRegionFilterOptions(rows) {
  const map = new Map();
  for (const r of rows) {
    if (!map.has(r.regionKey)) map.set(r.regionKey, r.regionName);
  }
  return [
    { value: "all", label: "All regions" },
    ...[...map.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
      .map(([value, label]) => ({ value, label })),
  ];
}

export function summarizeAprWorkflow(rows) {
  const totalDistricts = rows.length;
  let districtsStarted = 0;
  let districtsSubmittedRcc = 0;
  let districtsWithNdpc = 0;
  let closedReports = 0;

  const statusCounts = { Start: 0, Pending: 0, Completed: 0, Closed: 0, none: 0 };

  const districtsDoneList = [];

  for (const r of rows) {
    const st = r.status;
    if (!st) {
      statusCounts.none++;
      continue;
    }
    districtsStarted++;
    if (statusCounts[st] !== undefined) statusCounts[st]++;
    if (["Pending", "Completed", "Closed"].includes(st)) districtsSubmittedRcc++;
    if (["Completed", "Closed"].includes(st)) districtsWithNdpc++;
    if (st === "Closed") {
      closedReports++;
      districtsDoneList.push({
        districtId: r.districtId,
        name: r.districtName,
        regionName: r.regionName,
      });
    }
  }

  districtsDoneList.sort((a, b) =>
    a.regionName.localeCompare(b.regionName) || a.name.localeCompare(b.name),
  );

  const byRegion = new Map();
  for (const r of rows) {
    if (!byRegion.has(r.regionKey)) {
      byRegion.set(r.regionKey, { regionName: r.regionName, rows: [] });
    }
    byRegion.get(r.regionKey).rows.push(r);
  }

  let regionsStarted = 0;
  let regionsSubmittedNdpc = 0;
  const regionsDoneList = [];

  for (const [, grp] of byRegion) {
    const sts = grp.rows.map((x) => x.status);
    if (sts.some((s) => s != null)) regionsStarted++;
    if (sts.some((s) => s === "Completed" || s === "Closed")) regionsSubmittedNdpc++;
    const allClosed =
      grp.rows.length > 0 && grp.rows.every((x) => x.status === "Closed");
    if (allClosed) regionsDoneList.push(grp.regionName);
  }

  regionsDoneList.sort((a, b) => String(a).localeCompare(String(b)));

  const startedRate = pct(districtsStarted, totalDistricts);
  const rccRate = pct(districtsSubmittedRcc, totalDistricts);
  const ndpcRate = pct(districtsWithNdpc, totalDistricts);
  const closedRate = pct(closedReports, totalDistricts);

  const reportKpis = [
    {
      title: "District APR started rate",
      description: "Districts with an APR workflow record for the year",
      value: startedRate,
      unit: "%",
      status: derivedStatus(startedRate),
    },
    {
      title: "With NDPC pipeline rate",
      description: "Districts at Completed or Closed (forwarded from RCC)",
      value: ndpcRate,
      unit: "%",
      status: derivedStatus(ndpcRate),
    },
    // {
    //   title: "Closed report rate",
    //   description: "Districts with NDPC-closed APR",
    //   value: closedRate,
    //   unit: "%",
    //   status: derivedStatus(closedRate),
    // },
  ];

  const trendBars = [
    { stage: "No record", count: statusCounts.none },
    { stage: "Start", count: statusCounts.Start },
    { stage: "Pending (RCC)", count: statusCounts.Pending },
    { stage: "Completed (NDPC)", count: statusCounts.Completed },
    { stage: "Closed", count: statusCounts.Closed },
  ];

  return {
    totalDistricts,
    districtsStarted,
    regionsStarted,
    districtsSubmittedRcc,
    regionsSubmittedNdpc,
    closedReports,
    districtsDoneList,
    regionsDoneList,
    statusCounts,
    trendBars,
    reportKpis,
  };
}
